import { z } from "zod";

export const contractVersion = "1.0" as const;
export const admissionUpdateSchema = z
  .object({
    expectedVersion: z.number().int().min(1).max(999999999),
    minuteLimit: z.number().int().min(0).max(10000000),
    dayLimit: z.number().int().min(0).max(1000000000),
  })
  .strict();
export const admissionBudgetSchema = z
  .object({
    tenantId: z.uuid(),
    projectId: z.uuid(),
    environment: z.enum(["development", "staging", "production"]),
    version: z.number().int().positive(),
    minuteLimit: z.number().int().min(0).max(10000000),
    dayLimit: z.number().int().min(0).max(1000000000),
    minuteStart: z.iso.datetime({ offset: true }),
    dayStart: z.iso.date(),
    minuteAdmissions: z.number().int().nonnegative(),
    dayAdmissions: z.number().int().nonnegative(),
    basis: z.literal("application-request-reservations"),
    providerBillableUsage: z.literal(false),
  })
  .strict();
export const id = z.uuid();
export const environmentSchema = z.enum([
  "development",
  "staging",
  "production",
]);
export const roleSchema = z.enum(["customer", "agent", "manager", "auditor"]);
export const statusSchema = z.enum(["open", "waiting", "closed"]);
export const capabilitySchema = z.enum([
  "text",
  "attachments",
  "read-receipts",
]);
export const sessionClaimsSchema = z
  .object({
    schemaVersion: z.literal(contractVersion),
    iss: z.literal("showtimesoft-support"),
    aud: z.literal("support-api"),
    tenantId: id,
    projectId: id,
    environment: environmentSchema,
    actorId: id,
    role: roleSchema,
    sessionId: id,
    iat: z.number().int(),
    exp: z.number().int(),
  })
  .strict();
export type SessionClaims = z.infer<typeof sessionClaimsSchema>;
export type Scope = Pick<
  SessionClaims,
  "tenantId" | "projectId" | "environment"
>;
export const projectSchema = z
  .object({
    id,
    tenantId: id,
    name: z.string().trim().min(1).max(80),
    environment: environmentSchema,
    publicKey: z.string().min(8).max(100),
    allowedOrigins: z
      .array(
        z.url()
          .refine((v) => {
            const u = new URL(v);
            return (
              u.origin === v &&
              (u.protocol === "https:" ||
                (u.protocol === "http:" &&
                  ["localhost", "127.0.0.1"].includes(u.hostname)))
            );
          }),
      )
      .min(1)
      .max(10),
    contractVersion: z.literal(contractVersion),
    capabilities: z.array(capabilitySchema).min(1),
    status: z.enum(["draft", "testing", "active", "disabled"]),
    version: z.number().int().positive(),
    timezone: z
      .string()
      .max(80)
      .refine((v) => {
        try {
          new Intl.DateTimeFormat("en", { timeZone: v });
          return true;
        } catch {
          return false;
        }
      }),
    businessHours: z.string().max(300),
    unansweredThresholdMinutes: z
      .number()
      .int()
      .min(1)
      .max(10080)
      .nullable()
      .optional(),
    retentionDays: z.number().int().min(1).max(3650),
    connectorVersion: z.string().max(30),
    lastSelfTestAt: z.iso.datetime({ offset: true }).nullable(),
  })
  .strict();
export type Project = z.infer<typeof projectSchema>;
export const projectUpdateSchema = projectSchema
  .pick({
    name: true,
    allowedOrigins: true,
    capabilities: true,
    status: true,
    timezone: true,
    businessHours: true,
    unansweredThresholdMinutes: true,
    retentionDays: true,
    connectorVersion: true,
  })
  .partial()
  .extend({ expectedVersion: z.number().int().positive() })
  .strict()
  .refine((v) => Object.keys(v).length > 1);

export const conversationSchema = z
  .object({
    id,
    tenantId: id,
    projectId: id,
    customerId: id,
    title: z.string().max(160),
    status: statusSchema,
    assigneeId: id.nullable(),
    version: z.number().int().positive(),
    lastMessageSeq: z.number().int().nonnegative(),
    updatedAt: z.string(),
    unreadCount: z.number().int().nonnegative().default(0),
    awaitingReply: z.boolean().default(false),
    awaitingSince: z.iso.datetime({ offset: true })
      .nullable()
      .default(null),
  })
  .strict();
export type Conversation = z.infer<typeof conversationSchema>;
export const messageSchema = z
  .object({
    messageId: id,
    conversationId: id,
    clientMessageId: id,
    messageSeq: z.number().int().positive(),
    senderId: id,
    senderKind: z.enum(["customer", "agent", "ai"]),
    text: z.string().max(4000),
    attachmentIds: z.array(id).max(5),
    serverCreatedAt: z.string(),
  })
  .strict();
export type StoredMessage = z.infer<typeof messageSchema>;
export const sendMessageSchema = z
  .object({
    clientMessageId: id,
    text: z.string().trim().max(4000),
    attachmentIds: z.array(id).max(5).default([]),
  })
  .strict()
  .refine((v) => v.text.length > 0 || v.attachmentIds.length > 0);
export const assignmentSchema = z
  .object({
    expectedVersion: z.number().int().positive(),
    assigneeId: id.nullable(),
  })
  .strict();
export const statusCommandSchema = z
  .object({
    expectedVersion: z.number().int().positive(),
    status: statusSchema,
  })
  .strict();
export const readCursorSchema = z
  .object({ lastReadSeq: z.number().int().nonnegative() })
  .strict();
export const noteSchema = z
  .object({ text: z.string().trim().min(1).max(4000) })
  .strict();
export const subscriptionSchema = z
  .object({
    endpoint: z.url(),
    expirationTime: z.number().nonnegative().nullable().optional(),
    allProjects: z.boolean().optional(),
    keys: z
      .object({
        p256dh: z.string().min(40).max(200),
        auth: z.string().min(10).max(100),
      })
      .strict(),
  })
  .strict();
export const eventSchema = z
  .object({
    schemaVersion: z.literal(contractVersion),
    eventId: id,
    projectId: id,
    conversationId: id,
    occurredAt: z.string(),
    type: z.enum([
      "message.created",
      "conversation.updated",
      "assignment.updated",
      "read.updated",
      "attachment.ready",
      "sync.required",
    ]),
    message: messageSchema.optional(),
    version: z.number().int().optional(),
    lastReadSeq: z.number().int().optional(),
  })
  .strict();
export type SupportEvent = z.infer<typeof eventSchema>;
export class SupportError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    public readonly current?: unknown,
  ) {
    super(code);
  }
}
export function requireAgent(claims: SessionClaims, write = false): void {
  if (claims.role === "customer" || (write && claims.role === "auditor"))
    throw new SupportError(403, "forbidden");
}

export const inboxFiltersSchema = z
  .object({
    status: statusSchema.optional(),
    inbox: z
      .enum([
        "all",
        "open",
        "waiting",
        "closed",
        "mine",
        "unassigned",
        "unanswered",
      ])
      .optional(),
    query: z.string().trim().max(160).optional(),
    before: z.iso.datetime({ offset: true }).optional(),
    beforeId: id.optional(),
    allProjects: z.enum(["true", "false"]).optional(),
  })
  .strict()
  .refine((v) => !v.beforeId || Boolean(v.before));
export type InboxFilters = Omit<
  z.infer<typeof inboxFiltersSchema>,
  "allProjects"
>;

export const privacyPolicyCommandSchema = z
  .object({
    expectedVersion: z.number().int().nonnegative(),
    storageRegion: z.string().trim().min(1).max(80),
    noticeUrl: z
      .url()
      .max(2048)
      .refine((value) => new URL(value).protocol === "https:"),
    collectedFields: z
      .array(z.enum(["messages", "attachments", "contact", "internal-notes"]))
      .min(1)
      .max(4)
      .refine((items) => new Set(items).size === items.length),
    retentionDays: z.number().int().min(1).max(3650),
    backupExpiryDays: z.number().int().min(1).max(3650),
    status: z.enum(["draft", "approved"]),
    legalHold: z.boolean(),
  })
  .strict();
export const privacyGrantCommandSchema = z
  .object({
    expectedVersion: z.number().int().nonnegative(),
    actorId: id,
    canExport: z.boolean(),
    canDelete: z.boolean(),
    expiresAt: z.iso.datetime({ offset: true }),
  })
  .strict();
export type PrivacyPolicyCommand = z.infer<typeof privacyPolicyCommandSchema>;
export type PrivacyGrantCommand = z.infer<typeof privacyGrantCommandSchema>;
export const privacyPolicySchema = privacyPolicyCommandSchema
  .omit({ expectedVersion: true })
  .extend({
    version: z.number().int().positive(),
    approvedAt: z.iso.datetime({ offset: true }).nullable(),
    updatedAt: z.iso.datetime({ offset: true }),
  })
  .strict();
export const privacyStatusSchema = z
  .object({
    policy: privacyPolicySchema.nullable(),
    policyReady: z.boolean(),
    projectRetentionDays: z.number().int().min(1).max(3650),
    canExport: z.boolean(),
    canDelete: z.boolean(),
    canManagePrivacy: z.boolean(),
    grantVersion: z.number().int().nonnegative(),
    grantExpiresAt: z.iso.datetime({ offset: true }).nullable(),
    requiresReauthentication: z.literal(true),
    exportExecutionAvailable: z.literal(true),
    deleteExecutionAvailable: z.literal(false),
    deleteRequestAvailable: z.literal(true),
  })
  .strict();
export const privacyGrantQuerySchema = z.object({ actorId: id }).strict();
export const privacyGrantStateSchema = z
  .object({
    actorId: id,
    version: z.number().int().nonnegative(),
    canExport: z.boolean(),
    canDelete: z.boolean(),
    expiresAt: z.iso.datetime({ offset: true }).nullable(),
  })
  .strict();
export type PrivacyStatus = z.infer<typeof privacyStatusSchema>;
export type PrivacyGrantState = z.infer<typeof privacyGrantStateSchema>;
export const exportPrepareSchema = z
  .object({
    conversationId: id,
    includeInternalNotes: z.boolean().default(false),
  })
  .strict();
export const exportConfirmSchema = z
  .object({ requestId: id, authAccessToken: z.string().min(20).max(4096) })
  .strict();
export const exportConsumeSchema = z.object({ requestId: id }).strict();
export const deletionPreviewCommandSchema = z
  .object({ conversationId: id })
  .strict();
export const deletionCreateCommandSchema = z
  .object({
    conversationId: id,
    expectedVersion: z.number().int().positive(),
  })
  .strict();
export const deletionRequestCommandSchema = z
  .object({ requestId: id })
  .strict();
export const deletionCancelCommandSchema = z
  .object({
    requestId: id,
    expectedVersion: z.number().int().positive(),
  })
  .strict();
export const deletionReviewCommandSchema = deletionCancelCommandSchema
  .extend({
    decision: z.enum(["approved", "rejected"]),
    retentionReviewed: z.boolean(),
    legalHoldReviewed: z.boolean(),
    sourceOwnerReviewed: z.boolean(),
    backupPlanReviewed: z.boolean(),
  })
  .strict();
export const deletionPreviewSchema = z
  .object({
    mode: z.literal("dry-run"),
    conversationId: id,
    conversationVersion: z.number().int().positive(),
    status: statusSchema,
    lastMessageSeq: z.number().int().nonnegative(),
    messages: z.number().int().nonnegative(),
    internalNotes: z.number().int().nonnegative(),
    attachments: z.number().int().nonnegative(),
    attachmentBytes: z.number().int().nonnegative(),
    readCursors: z.number().int().nonnegative(),
    outboxEvents: z.number().int().nonnegative(),
    exportRequests: z.number().int().nonnegative(),
    sourceLinks: z.number().int().nonnegative(),
    customerHasOtherConversations: z.boolean(),
    retentionDays: z.number().int().positive(),
    backupExpiryDays: z.number().int().positive(),
    executionAvailable: z.literal(false),
    scope: z.literal("support-conversation"),
    sourceRecordsIncluded: z.literal(false),
    backupDeletionVerified: z.literal(false),
  })
  .strict();
export const deletionStatusSchema = z.enum([
  "pending-review",
  "approved",
  "rejected",
  "cancelled",
  "erasing",
  "database-deleted",
]);
export const deletionListQuerySchema = z
  .object({
    status: deletionStatusSchema.optional(),
    before: z.iso.datetime({ offset: true }).optional(),
    beforeId: id.optional(),
  })
  .strict()
  .refine((query) => Boolean(query.before) === Boolean(query.beforeId), {
    message: "Both cursor fields are required together",
  });
export const deletionListItemSchema = z
  .object({
    requestId: id,
    conversationId: id,
    status: deletionStatusSchema,
    version: z.number().int().positive(),
    requestedBy: id,
    requestedAt: z.iso.datetime({ offset: true }),
    reviewedAt: z.iso.datetime({ offset: true }).nullable(),
  })
  .strict();
export const deletionListSchema = z
  .object({
    items: z.array(deletionListItemSchema).max(50),
    nextCursor: z
      .object({ before: z.iso.datetime({ offset: true }), beforeId: id })
      .strict()
      .nullable(),
  })
  .strict();
export type DeletionListQuery = z.infer<typeof deletionListQuerySchema>;
export type DeletionList = z.infer<typeof deletionListSchema>;
export const deletionRequestSchema = z
  .object({
    requestId: id,
    conversationId: id,
    status: deletionStatusSchema,
    version: z.number().int().positive(),
    requestedBy: id,
    requestedAt: z.iso.datetime({ offset: true }),
    reviewedBy: id.nullable(),
    reviewedAt: z.iso.datetime({ offset: true }).nullable(),
    review: z
      .object({
        retentionReviewed: z.boolean(),
        legalHoldReviewed: z.boolean(),
        sourceOwnerReviewed: z.boolean(),
        backupPlanReviewed: z.boolean(),
      })
      .strict()
      .nullable(),
    policyVersion: z.number().int().positive(),
    grantVersion: z.number().int().positive(),
    snapshot: deletionPreviewSchema,
    executionAvailable: z.literal(false),
  })
  .strict();
export type DeletionPreview = z.infer<typeof deletionPreviewSchema>;
export type DeletionRequest = z.infer<typeof deletionRequestSchema>;
export type DeletionReviewCommand = z.infer<typeof deletionReviewCommandSchema>;
export const deletionReconcileSchema = deletionReviewCommandSchema
  .extend({ decision: z.literal("approved") })
  .strict();
export type DeletionReconcileCommand = z.infer<typeof deletionReconcileSchema>;
export const deletionAuthPrepareSchema = deletionCancelCommandSchema;
export const deletionAuthCommandSchema = z
  .object({ authorizationId: id })
  .strict();
export const deletionAuthConfirmSchema = deletionAuthCommandSchema
  .extend({ authAccessToken: z.string().min(20).max(4096) })
  .strict();
export const preparedDeletionAuthSchema = z
  .object({
    authorizationId: id,
    deletionRequestId: id,
    requestVersion: z.number().int().positive(),
    expiresAt: z.iso.datetime({ offset: true }),
    authenticateAfter: z.number().int().positive(),
    executionAvailable: z.literal(false),
  })
  .strict();
export const confirmedDeletionAuthSchema = z
  .object({
    authorizationId: id,
    deletionRequestId: id,
    confirmed: z.literal(true),
    expiresAt: z.iso.datetime({ offset: true }),
    executionAvailable: z.literal(false),
  })
  .strict();
export type PreparedDeletionAuth = z.infer<typeof preparedDeletionAuthSchema>;
export type ConfirmedDeletionAuth = z.infer<typeof confirmedDeletionAuthSchema>;
export const deletionJobSchema = z
  .object({
    version: z.number().int().positive(),
    jobId: id,
    requestId: id,
    state: z.enum(["deleting-files", "files-deleted", "database-deleted"]),
    createdAt: z.iso.datetime({ offset: true }),
    filesDeletedAt: z.iso.datetime({ offset: true }).nullable(),
    totalFiles: z.number().int().nonnegative(),
    deletedFiles: z.number().int().nonnegative(),
    databaseDeletedAt: z.iso.datetime({ offset: true }).nullable(),
    customerDeleted: z.boolean().nullable(),
    backupExpiryDue: z.iso.datetime({ offset: true }).nullable(),
    databaseDeletionVerified: z.boolean(),
    backupDeletionVerified: z.literal(false),
    cacheDeletionVerified: z.literal(false),
    sourceRecordsIncluded: z.literal(false),
    completed: z.literal(false),
  })
  .strict();
export type DeletionJob = z.infer<typeof deletionJobSchema>;
export const deletionFinalizeSchema = z
  .object({ requestId: id, jobVersion: z.number().int().positive() })
  .strict();
export const exportFileSchema = z
  .object({ requestId: id, attachmentId: id })
  .strict();
export const preparedExportSchema = z
  .object({
    requestId: id,
    conversationId: id,
    expiresAt: z.iso.datetime({ offset: true }),
    authenticateAfter: z.number().int().positive(),
    includeInternalNotes: z.boolean(),
  })
  .strict();
export const supportExportSchema = z
  .object({
    schemaVersion: z.literal(contractVersion),
    exportId: id,
    exportedAt: z.iso.datetime({ offset: true }),
    tenantId: id,
    projectId: id,
    environment: environmentSchema,
    conversation: z
      .object({
        id,
        title: z.string().max(160),
        status: statusSchema,
        lastMessageSeq: z.number().int().nonnegative(),
      })
      .strict(),
    customer: z.object({ id, displayName: z.string().nullable() }).strict(),
    messages: z.array(messageSchema).max(1000),
    internalNotes: z
      .array(
        z
          .object({
            id,
            text: z.string().max(4000),
            createdAt: z.iso.datetime({ offset: true }),
          })
          .strict(),
      )
      .max(1000),
    attachments: z.array(
      z
        .object({
          id,
          name: z.string(),
          mime: z.string(),
          size: z.number().int().positive(),
          status: z.enum(["pending", "quarantined", "ready", "rejected"]),
        })
        .strict(),
    ),
    attachmentContentIncluded: z.literal(false),
    policyVersion: z.number().int().positive(),
    includeInternalNotes: z.boolean(),
  })
  .strict();
export type PreparedExport = z.infer<typeof preparedExportSchema>;
export type SupportExport = z.infer<typeof supportExportSchema>;

export const messageUsageSchema = z
  .object({
    tenantId: id,
    projectId: id,
    environment: environmentSchema,
    periodStart: z.iso.datetime({ offset: true }),
    periodEnd: z.iso.datetime({ offset: true }),
    meteringStartedAt: z.iso.datetime({ offset: true }),
    measurementStart: z.iso.datetime({ offset: true }),
    measurementEnd: z.iso.datetime({ offset: true }),
    coversMonthStart: z.boolean(),
    customerMessages: z.number().int().nonnegative(),
    agentMessages: z.number().int().nonnegative(),
    textBytes: z.number().int().nonnegative(),
    basis: z.literal("transaction-inserts-utf8"),
    providerBillableUsage: z.literal(false),
  })
  .strict();

export const holidayDatesSchema = z
  .array(z.iso.date().refine((v) => v >= "0001-01-01"))
  .max(366)
  .refine((v) => new Set(v).size === v.length);
export const holidayUpdateSchema = z
  .object({
    expectedVersion: z.number().int().positive().max(999999999),
    holidayDates: holidayDatesSchema,
  })
  .strict();
export const projectHolidaysSchema = z
  .object({
    tenantId: id,
    projectId: id,
    environment: environmentSchema,
    timezone: projectSchema.shape.timezone,
    businessHours: z.string().max(300),
    holidayDates: holidayDatesSchema,
    version: z.number().int().positive(),
    localDate: z.iso.date(),
    isHoliday: z.boolean(),
  })
  .strict();
export type ProjectHolidays = z.infer<typeof projectHolidaysSchema>;

export const attachmentUsageSchema = messageUsageSchema
  .omit({
    customerMessages: true,
    agentMessages: true,
    textBytes: true,
    basis: true,
  })
  .extend({
    storedObjects: z.number().int().nonnegative(),
    storedBytes: z.number().int().nonnegative(),
    basis: z.literal("first-verified-r2-object"),
  })
  .strict();

export const downloadUsageSchema = messageUsageSchema
  .omit({
    customerMessages: true,
    agentMessages: true,
    textBytes: true,
    basis: true,
  })
  .extend({
    completedStreams: z.number().int().nonnegative(),
    cancelledStreams: z.number().int().nonnegative(),
    failedStreams: z.number().int().nonnegative(),
    responseBytes: z.number().int().nonnegative(),
    basis: z.literal("response-stream-terminal-observations"),
  })
  .strict();

export const jobUsageSchema = messageUsageSchema
  .omit({
    customerMessages: true,
    agentMessages: true,
    textBytes: true,
    basis: true,
  })
  .extend({
    counts: z
      .array(
        z
          .object({
            kind: z.enum(["queue-publish", "realtime", "inbox", "push"]),
            leaseAcquisitions: z.number().int().nonnegative(),
            retryLeases: z.number().int().nonnegative(),
            successfulFinishes: z.number().int().nonnegative(),
            failedFinishes: z.number().int().nonnegative(),
            terminalFailures: z.number().int().nonnegative(),
          })
          .strict()
          .refine((v) => v.retryLeases <= v.leaseAcquisitions),
      )
      .length(4)
      .refine((v) => new Set(v.map((row) => row.kind)).size === 4),
    basis: z.literal("committed-job-state-transitions"),
  })
  .strict();

export const connectorKeyStateSchema = z
  .object({
    projectId: id,
    environment: environmentSchema,
    version: z.number().int().nonnegative(),
    keyPrefix: z.string().nullable(),
    createdAt: z.iso.datetime({ offset: true }).nullable(),
    expiresAt: z.iso.datetime({ offset: true }).nullable(),
    active: z.boolean(),
  })
  .strict();
export const connectorKeyCommandSchema = z
  .object({ expectedVersion: z.number().int().nonnegative() })
  .strict();
export const connectorKeyIssuedSchema = z
  .object({
    credential: z.string().regex(/^shc_[a-f0-9]{64}$/),
    state: connectorKeyStateSchema,
  })
  .strict();
